import type { ConsoleVersion } from './queries/useConsoleVersion';

/**
 * Identity of the build a tab is running, and the comparison against what is
 * currently deployed. Kept out of the hook module so it stays free of
 * `import.meta.env`, which ts-jest cannot parse under CommonJS.
 */

/**
 * The entry chunk this tab actually booted from, e.g. `/assets/index-BYpxAgtn.js`.
 * Read from the DOM rather than baked in at build time so it is by definition
 * the build that is running.
 *
 * Returns undefined under the Vite dev server, where the entry is `/src/main.tsx`
 * rather than a hashed asset.
 */
export function getLoadedBuildId(): string | undefined {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')
  );

  const entry = scripts.find((script) =>
    toPathname(script.src)?.startsWith('/assets/')
  );

  return entry ? toPathname(entry.src) : undefined;
}

function toPathname(src: string): string | undefined {
  try {
    return new URL(src, globalThis.location.href).pathname;
  } catch {
    return undefined;
  }
}

/**
 * Prefer `buildId` — the deployed entry-chunk path. Vite rehashes it on every
 * deployment that shipped new code, so it detects deployments the project
 * version cannot: dev deploys on every merge to develop while package.json only
 * bumps on release, which left tabs running builds whose lazy chunks Pages had
 * already stopped serving.
 *
 * `deployedVersion` (REACT_APP_VERSION) is only the fallback, for the Docker
 * path where the Express endpoint returns no buildId.
 */
export function isNewDeployment(
  latest: ConsoleVersion | undefined,
  loadedBuildId: string | undefined,
  deployedVersion: string | undefined
): boolean {
  if (latest?.buildId && loadedBuildId) {
    return latest.buildId !== loadedBuildId;
  }

  if (!deployedVersion || !latest?.v) {
    return false;
  }

  return latest.v !== deployedVersion;
}
